import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { Calendar, User, ArrowLeft, Share2, Tag } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const res = await axios.get(`${API}/blog/${slug}`);
        setPost(res.data);
        document.title = `${res.data.title} | Amar Preet Scaffolding`;
        
        // Fetch related posts
        const allPosts = await axios.get(`${API}/blog`);
        setRelatedPosts(allPosts.data.filter(p => p.slug !== slug && p.category === res.data.category).slice(0, 3));
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-4xl font-bold text-secondary mb-4">Post Not Found</h1>
          <Link to="/blog" className="text-primary hover:underline">Back to Blog</Link>
        </div>
      </div>
    );
  }

  // Parse markdown-like content to HTML
  const renderContent = (content) => {
    const lines = content.split('\n');
    let html = [];
    let inList = false;

    lines.forEach((line, index) => {
      if (line.startsWith('## ')) {
        if (inList) { html.push('</ul>'); inList = false; }
        html.push(`<h2 class="font-heading text-2xl font-bold text-secondary uppercase tracking-tight mt-8 mb-4">${line.slice(3)}</h2>`);
      } else if (line.startsWith('### ')) {
        if (inList) { html.push('</ul>'); inList = false; }
        html.push(`<h3 class="font-heading text-xl font-bold text-secondary uppercase tracking-tight mt-6 mb-3">${line.slice(4)}</h3>`);
      } else if (line.startsWith('- ')) {
        if (!inList) { html.push('<ul class="list-disc list-inside space-y-2 my-4 text-muted-foreground">'); inList = true; }
        html.push(`<li>${line.slice(2)}</li>`);
      } else if (line.startsWith('1. ') || line.startsWith('2. ') || line.startsWith('3. ')) {
        if (inList) { html.push('</ul>'); inList = false; }
        html.push(`<p class="text-muted-foreground leading-relaxed my-2">${line}</p>`);
      } else if (line.trim() === '') {
        if (inList) { html.push('</ul>'); inList = false; }
      } else if (line.startsWith('**') && line.endsWith('**')) {
        html.push(`<p class="font-bold text-secondary my-4">${line.slice(2, -2)}</p>`);
      } else {
        if (inList) { html.push('</ul>'); inList = false; }
        html.push(`<p class="text-muted-foreground leading-relaxed my-4">${line}</p>`);
      }
    });

    if (inList) html.push('</ul>');
    return html.join('');
  };

  return (
    <div data-testid="blog-post-page">
      {/* Hero */}
      <section className="bg-secondary py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          {/* Breadcrumb */}
          <nav className="mb-6">
            <Link to="/blog" className="inline-flex items-center gap-2 text-slate-400 hover:text-primary transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Back to Blog
            </Link>
          </nav>
          
          {/* Category */}
          <span className="inline-block text-xs font-bold uppercase tracking-wider px-3 py-1 bg-primary text-primary-foreground mb-4">
            {post.category}
          </span>
          
          {/* Title */}
          <h1 className="font-heading text-3xl md:text-5xl font-black text-white uppercase tracking-tighter leading-tight mb-6" data-testid="post-title">
            {post.title}
          </h1>
          
          {/* Meta */}
          <div className="flex flex-wrap items-center gap-6 text-slate-400">
            <span className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {new Date(post.created_at).toLocaleDateString('en-IN', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </span>
            <span className="flex items-center gap-2">
              <User className="w-4 h-4" />
              {post.author}
            </span>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      {post.featured_image && (
        <div className="bg-white py-8">
          <div className="container mx-auto px-4 md:px-8 max-w-4xl">
            <img
              src={post.featured_image}
              alt={post.title}
              className="w-full h-[400px] object-cover"
            />
          </div>
        </div>
      )}

      {/* Content */}
      <section className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <article 
            className="prose prose-lg max-w-none"
            dangerouslySetInnerHTML={{ __html: renderContent(post.content) }}
            data-testid="post-content"
          />

          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="mt-12 pt-8 border-t border-border">
              <div className="flex flex-wrap items-center gap-3">
                <Tag className="w-5 h-5 text-muted-foreground" />
                {post.tags.map((tag) => (
                  <span 
                    key={tag} 
                    className="bg-muted px-3 py-1 text-sm text-muted-foreground"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* CTA Box */}
          <div className="mt-12 bg-primary p-8 text-center">
            <h3 className="font-heading text-2xl font-bold text-primary-foreground uppercase tracking-tight mb-4">
              Need Scaffolding for Your Project?
            </h3>
            <p className="text-primary-foreground/80 mb-6">
              Get a free quote from India's trusted scaffolding provider
            </p>
            <Link
              to="/contact"
              className="inline-block bg-secondary text-secondary-foreground font-bold uppercase tracking-wider px-6 py-3 hover:bg-secondary/90 transition-all"
              data-testid="post-cta"
            >
              Get Free Quote
            </Link>
          </div>
        </div>
      </section>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-16 bg-muted">
          <div className="container mx-auto px-4 md:px-8 max-w-7xl">
            <h2 className="font-heading text-2xl font-bold text-secondary uppercase tracking-tight mb-8 text-center">
              Related Articles
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedPosts.map((relPost) => (
                <Link 
                  key={relPost.id} 
                  to={`/blog/${relPost.slug}`}
                  className="bg-white p-6 border border-border hover:border-primary transition-colors"
                >
                  <span className="text-xs font-bold uppercase tracking-wider text-primary mb-2 block">
                    {relPost.category}
                  </span>
                  <h3 className="font-heading font-bold text-secondary uppercase tracking-tight mb-2">
                    {relPost.title}
                  </h3>
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {relPost.meta_description}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
